import { z } from 'zod';
import { HI_TYPES } from '../constants';
import { consentCovers, type ConsentTerms } from '../consent';
import { utf8 } from '../encoding';
import { buildPatient } from '../fhir/build';
import { checkDocument } from '../fhir/check';
import { documentDateOf, patientDetails } from '../fhir/summary';
import type { Bundle, Patient } from '../fhir/types';
import type { DeviceKeys, PeerDevice } from '../keys';
import { jsonBytes, sealEnvelope } from '../seal';
import { withStoreLock } from './engine';
import { MAX_RECORD_BYTES, categoryOfDocument, type HeldRecord, type RecordEntry, type RecordStore } from './records';

const objectId = z.string().regex(/^[a-f0-9]{24}$/, 'Must be an ID');

/**
 * What one history_share envelope carries: one document, under one consent.
 * The receiver reads the document's date from the document itself and checks
 * it against the consent, rather than trusting the sender.
 */
export const historySharePayloadSchema = z.strictObject({
  v: z.literal(1),
  consentId: objectId,
  patientId: objectId,
  record: z.strictObject({
    /** The sender's own ID and version for the document, so a resend updates the copy. */
    id: z.uuid(),
    version: z.number().int().min(1),
    hiType: z.enum(HI_TYPES),
    resource: z.record(z.string(), z.unknown()),
  }),
});
export type HistorySharePayload = z.infer<typeof historySharePayloadSchema>;

/** Why a received share was not stored. The message never includes content. */
export class ShareRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShareRefusal';
  }
}

const byteLength = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;

/** A practice's own patient records linked to this Curentia account, oldest first. */
export async function patientsOfAccount(store: RecordStore, accountId: string): Promise<RecordEntry[]> {
  return (await store.since(0)).filter((entry) => entry.kind === 'patient' && entry.accountId === accountId);
}

/**
 * The documents of these patients that the consent covers, oldest first. A
 * holder passes on only the records it made itself: copies it received from
 * other practices or the patient stay where they are, so every record has one
 * source.
 */
export async function documentsUnder(store: RecordStore, patientIds: readonly string[], terms: ConsentTerms): Promise<HeldRecord[]> {
  const patients = new Set(patientIds);
  const entries = (await store.since(0)).filter(
    (entry) =>
      entry.kind === 'document' &&
      patients.has(entry.patientId) &&
      !entry.source &&
      entry.hiType !== undefined &&
      consentCovers(terms, entry.hiType, entry.date ?? entry.createdAt),
  );
  const records: HeldRecord[] = [];
  for (const entry of entries) {
    const record = await store.get(entry.id);
    if (record) records.push(record);
  }
  return records;
}

/**
 * The ID a receiving holder gives a shared document: the same for every
 * resend from the same sender, so a repeat updates the copy rather than
 * adding another. A UUID in the RFC 9562 version 8 layout.
 */
export async function sharedRecordId(sender: string, recordId: string): Promise<string> {
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', utf8(`curentia/v1/shared-record\0${sender}\0${recordId}`)));
  const bytes = digest.slice(0, 16);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x80;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Seals one history_share envelope per document and recipient device. */
export async function sealHistoryShares(input: {
  sender: DeviceKeys;
  /** Set when the sender is a practice's record holder. */
  fromPractice?: string;
  terms: ConsentTerms;
  documents: HeldRecord[];
  recipients: PeerDevice[];
}): Promise<{ header: string; ciphertext: string; signature: string }[]> {
  const wires: { header: string; ciphertext: string; signature: string }[] = [];
  for (const document of input.documents) {
    if (!document.hiType) continue;
    const payload: HistorySharePayload = {
      v: 1,
      consentId: input.terms.id,
      patientId: input.terms.patientId,
      record: { id: document.id, version: document.version, hiType: document.hiType, resource: document.resource },
    };
    for (const recipient of input.recipients) {
      const sealed = await sealEnvelope({
        kind: 'history_share',
        sender: input.sender,
        recipient,
        plaintext: jsonBytes(payload),
        ...(input.fromPractice ? { fromPractice: input.fromPractice } : {}),
        toPractice: input.terms.requesterPracticeId,
        consent: input.terms.id,
        hiType: document.hiType,
      });
      wires.push(sealed.wire);
    }
  }
  return wires;
}

/** Who sent a share: the patient's own phone, or a practice the patient named as a source. */
export type ShareSender = { kind: 'patient' } | { kind: 'practice'; practiceId: string; practiceName?: string };

/** The demographics a receiving practice starts from when it has no record for the patient yet: the document's own Patient. */
function patientFromDocument(resource: Record<string, unknown>, accountId: string): Patient {
  const bundle = resource as unknown as Bundle;
  const composition = bundle.entry?.[0]?.resource;
  const subject = composition?.resourceType === 'Composition' ? composition.subject.reference : undefined;
  const found = bundle.entry?.find((entry) => entry.fullUrl === subject)?.resource;
  const details = patientDetails(found);
  return buildPatient({
    name: details.name,
    ...(details.gender ? { gender: details.gender } : {}),
    ...(details.birthDate ? { birthDate: details.birthDate } : {}),
    accountId,
  });
}

/**
 * Stores one received document at the requesting practice's record holder,
 * under its own patient record for the consent's patient. The caller has
 * checked the consent and the sender; this checks the document: its structure,
 * size, type, and that its own date falls within the dates the patient
 * approved. If the practice has no record for the patient yet, one is made
 * from the document's Patient. A resend of a version already stored changes
 * nothing.
 */
export async function takeShare(
  store: RecordStore,
  input: { payload: HistorySharePayload; terms: ConsentTerms; sender: ShareSender; senderId: string; now: Date },
): Promise<HeldRecord> {
  const { payload, terms, sender } = input;
  if (payload.consentId !== terms.id || payload.patientId !== terms.patientId) throw new ShareRefusal('The share does not match its consent');
  const { hiType, resource } = payload.record;
  const [issue] = checkDocument(resource, hiType);
  if (issue) throw new ShareRefusal(`The document is not valid: ${issue.path} ${issue.message}`.slice(0, 300));
  if (byteLength(resource) > MAX_RECORD_BYTES) throw new ShareRefusal('The document is too large');
  const date = documentDateOf(resource);
  if (!date || !consentCovers(terms, hiType, date)) throw new ShareRefusal('The document is outside what the patient approved');
  const id = await sharedRecordId(sender.kind === 'patient' ? `patient:${terms.patientId}` : `practice:${sender.practiceId}`, payload.record.id);
  const at = input.now.toISOString();

  return withStoreLock(store, async () => {
    const existing = await store.get(id);
    if (existing && (existing.source?.version ?? 0) >= payload.record.version) return existing;
    let patientId = existing?.patientId ?? (await patientsOfAccount(store, terms.patientId))[0]?.id;
    if (!patientId) {
      patientId = globalThis.crypto.randomUUID();
      await store.save({
        id: patientId,
        patientId,
        kind: 'patient',
        category: 'demographics',
        version: 1,
        resource: patientFromDocument(resource, terms.patientId) as unknown as Record<string, unknown>,
        accountId: terms.patientId,
        createdAt: at,
        createdBy: input.senderId,
        updatedAt: at,
        updatedBy: input.senderId,
      });
    }
    return store.save({
      id,
      patientId,
      kind: 'document',
      category: categoryOfDocument(hiType),
      hiType,
      version: (existing?.version ?? 0) + 1,
      resource,
      date,
      source: {
        consentId: terms.id,
        from: sender.kind,
        ...(sender.kind === 'practice' ? { practiceId: sender.practiceId, ...(sender.practiceName ? { practiceName: sender.practiceName.slice(0, 120) } : {}) } : {}),
        recordId: payload.record.id,
        version: payload.record.version,
        receivedAt: at,
      },
      createdAt: existing?.createdAt ?? at,
      createdBy: existing?.createdBy ?? input.senderId,
      updatedAt: at,
      updatedBy: input.senderId,
    });
  });
}
